import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type ConnectorSummary } from "../lib/api";
import { AddSourceDialog } from "../components/AddSourceDialog";
import { ConfigureConnectorModal } from "../components/plc/ConfigureConnectorModal";
import { PlatformLogo } from "../components/PlatformLogo";
import { getPlatformLogo } from "../lib/platformLogos";
import { cn } from "../lib/cn";

interface SourceRow {
  id: string;
  name: string;
  connectorId: string;
  enabled: boolean;
  lastStatus?: string;
}

// Editorial taxonomy — each connector ID maps to a category and a one-line
// pitch. Drives the per-card description text. Adding a new connector is
// just another entry here.
const CONNECTOR_INFO: Record<
  string,
  {
    category: "PLC" | "SCADA" | "Historian" | "Database" | "Edge" | "OPC" | "CNC" | "Generic";
    tagline: string;
  }
> = {
  generic_ot_metadata: {
    category: "Generic",
    tagline: "Normalized CSV metadata source for any supported OT platform.",
  },
  kepware: {
    category: "OPC",
    tagline: "Kepware OPC aggregator — projects, channels, devices, tags.",
  },
  ignition: {
    category: "SCADA",
    tagline: "Inductive Ignition tag exports — atomic tags grouped by device.",
  },
  osisoft: {
    category: "Historian",
    tagline: "OSI PI Asset Framework — element templates, instances, attributes.",
  },
  litmus: {
    category: "Edge",
    tagline: "Litmus Edge gateway — devices, twin models, flows, analytics.",
  },
  siemens: {
    category: "PLC",
    tagline: "Siemens — tag table CSV exports plus optional web scrape.",
  },
  allenbradley: {
    category: "PLC",
    tagline: "Allen-Bradley — .L5X / .L5K / Micro-800 CSV; UDT-aware; web scrape.",
  },
  mssql: {
    category: "Database",
    tagline: "Microsoft SQL Server — tables, views, columns from any schema.",
  },
  postgres: {
    category: "Database",
    tagline: "PostgreSQL — tables, views, and columns across all user schemas.",
  },
  mitsubishi: {
    category: "PLC",
    tagline: "Mitsubishi MELSEC — GX Works CSV/XML/GX Works2/GX Works3 (.gx3) project exports.",
  },
  beckhoff: {
    category: "PLC",
    tagline: "Beckhoff TwinCAT — PLCopen XML exports; GVLs and Programs.",
  },
  matrikon: {
    category: "OPC",
    tagline: "Matrikon OPC — CSV exports; OPC groups as datasets.",
  },
  delta: {
    category: "PLC",
    tagline: "Delta PLC — label CSV exports; IEC 61131-3 tags.",
  },
  ls_electric: {
    category: "PLC",
    tagline: "LS Electric XG5000 — .XGP/.cmt projects and symbol table exports.",
  },
  automation_direct: {
    category: "PLC",
    tagline: "AutomationDirect — CSV tag exports with System ID metadata.",
  },
  panasonic: {
    category: "PLC",
    tagline: "Panasonic FPWIN Pro — .asc project exports; variables and config.",
  },
  codesys: {
    category: "PLC",
    tagline: "CODESYS — .sym symbol files, PLCopen XML, or CSV exports.",
  },
  opto22: {
    category: "PLC",
    tagline: "Opto22 SNAP PAC — variable CSV exports; typed tags from PAC Control.",
  },
  koyo: {
    category: "PLC",
    tagline: "Koyo PLC — Click and Direct tag CSV exports; address + data-type per tag.",
  },
  ge: {
    category: "PLC",
    tagline:
      "GE Proficy Machine Edition — .SwxCF/.BAK/.zip exports; Fanuc 9030, EGD PME, and VersaMax sub-types.",
  },
  fanuc: {
    category: "CNC",
    tagline:
      "FANUC CNC (FOCAS) — backup ZIP (PMC1.000 + SRAM_BAK.001); machine identity, alarm/message catalog, programs, offsets, macros.",
  },
};

// Fixed display order
const DISPLAY_ORDER = [
  "litmus",
  "kepware",
  "matrikon",
  "ignition",
  "osisoft",
  "mssql",
  "postgresql",
  "allenbradley",
  "siemens",
  "mitsubishi",
  "beckhoff",
  "delta",
  "ls_electric",
  "automation_direct",
  "panasonic",
  "codesys",
  "opto22",
  "koyo",
  "ge",
  "fanuc",
  "generic_ot_metadata",
];

const CATEGORY_TONE: Record<string, { chip: string; icon: string }> = {
  PLC: {
    chip: "bg-amber-50 text-amber-700 border-amber-500/30",
    icon: "bg-amber-100 text-amber-700",
  },
  SCADA: {
    chip: "bg-brand-50 text-brand-700 border-brand-500/30",
    icon: "bg-brand-100 text-brand-700",
  },
  Historian: {
    chip: "bg-rose-50 text-rose-700 border-rose-500/30",
    icon: "bg-rose-100 text-rose-700",
  },
  Database: {
    chip: "bg-emerald-50 text-emerald-700 border-emerald-500/30",
    icon: "bg-emerald-100 text-emerald-700",
  },
  Edge: {
    chip: "bg-litmus-light-green-100 text-litmus-dark-green-700 border-litmus-light-green-300",
    icon: "bg-litmus-light-green-100 text-litmus-dark-green-700",
  },
  OPC: {
    chip: "bg-violet-50 text-violet-700 border-violet-500/30",
    icon: "bg-violet-100 text-violet-700",
  },
  CNC: {
    chip: "bg-orange-50 text-orange-700 border-orange-500/30",
    icon: "bg-orange-100 text-orange-700",
  },
  Generic: {
    chip: "bg-sky-50 text-sky-700 border-sky-500/30",
    icon: "bg-sky-100 text-sky-700",
  },
};

const CATEGORY_ICONS: Record<string, JSX.Element> = {
  PLC: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <rect x="2" y="5" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M6 9h2m4 0h2M6 11h2m4 0h2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  SCADA: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Historian: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M3 14l4-4 3 3 4-5 3 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Database: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <ellipse cx="10" cy="6" rx="7" ry="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3 6v4c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5V6"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M3 10v4c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5v-4"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  ),
  Edge: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M10 3L3 7v6l7 4 7-4V7l-7-4Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M10 3v10M3 7l7 4 7-4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  OPC: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 3v2M10 15v2M3 10h2M15 10h2M5.05 5.05l1.41 1.41M13.54 13.54l1.41 1.41M5.05 14.95l1.41-1.41M13.54 6.46l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  CNC: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 3v3M10 14v3M3 10h3M14 10h3M5.3 5.3l2.1 2.1M12.6 12.6l2.1 2.1M5.3 14.7l2.1-2.1M12.6 7.4l2.1-2.1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  Generic: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M4 6h12M4 10h8M4 14h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const GENERIC_TEMPLATE = "Tag Name,Data Type,Address,Comment\n";

export function ConnectorsPage() {
  // Litmus Edge uses AddSourceDialog (its scheduled logic is schema-driven via ingestion_mode)
  const [addingFor, setAddingFor] = useState<ConnectorSummary | null>(null);
  // All other connectors use ConfigureConnectorModal (includes scheduled sync with SHA-256 detection)
  const [configuringConnector, setConfiguringConnector] = useState<ConnectorSummary | null>(null);

  const [filter, setFilter] = useState<string>("ALL");
  const [search, setSearch] = useState<string>("");

  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.get<ConnectorSummary[]>("/api/v1/connectors"),
  });
  const sources = useQuery({
    queryKey: ["sources"],
    queryFn: () => api.get<SourceRow[]>("/api/v1/sources"),
  });

  const sourceCountByConnector = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of sources.data ?? []) {
      m.set(s.connectorId, (m.get(s.connectorId) ?? 0) + 1);
    }
    return m;
  }, [sources.data]);

  const filtered = useMemo(() => {
    const list = connectors.data ?? [];
    const q = search.trim().toLowerCase();

    // Sort by DISPLAY_ORDER first, unknown connectors go to end alphabetically
    const sorted = [...list].sort((a, b) => {
      const ai = DISPLAY_ORDER.indexOf(a.id);
      const bi = DISPLAY_ORDER.indexOf(b.id);
      if (ai === -1 && bi === -1) return a.displayName.localeCompare(b.displayName);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return sorted.filter((c) => {
      const info = CONNECTOR_INFO[c.id];
      const matchesCategory = filter === "ALL" || (info?.category ?? "Other") === filter;
      const matchesSearch =
        !q ||
        c.id.toLowerCase().includes(q) ||
        c.displayName.toLowerCase().includes(q) ||
        (info?.tagline ?? "").toLowerCase().includes(q) ||
        (info?.category ?? "").toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [connectors.data, filter, search]);

  const categories = ["ALL", "PLC", "OPC", "SCADA", "Historian", "Database", "Edge", "CNC", "Generic"];

  // Category counts for filter badges
  const categoryCounts = useMemo(() => {
    const m: Record<string, number> = { ALL: connectors.data?.length ?? 0 };
    for (const c of connectors.data ?? []) {
      const cat = CONNECTOR_INFO[c.id]?.category ?? "Other";
      m[cat] = (m[cat] ?? 0) + 1;
    }
    return m;
  }, [connectors.data]);

  function downloadGenericTemplate() {
    const blob = new Blob([GENERIC_TEMPLATE], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "generic_ot_metadata_template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="page-container">
      {/* PAGE HEADER */}
      <header className="pt-8 pb-6">
        <div className="eyebrow mb-1">Library</div>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] font-semibold leading-tight tracking-tight text-ink">
              Connectors
            </h1>
            <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-ash">
              Each connector parses a source backup, export, or server to extract metadata. Pick one
              to add a source.
            </p>
          </div>
          <div className="shrink-0 rounded-lg border border-rule bg-surface px-4 py-2.5 text-center">
            <div className="font-display text-[28px] font-semibold leading-none text-ink" data-num>
              {connectors.data?.length ?? "—"}
            </div>
            <div className="mt-0.5 font-mono text-[9.5px] uppercase tracking-wider text-ash">
              connectors
            </div>
          </div>
        </div>
      </header>

      {/* SEARCH + FILTER BAR */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ash"
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M10.5 10.5L14 14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="text"
            placeholder="Search connectors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-datahub-gray-1700/20 bg-surface py-2 pl-9 pr-4 text-[13px] text-ink shadow-sm placeholder:text-ash/60 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ash hover:text-ink"
              aria-label="Clear search"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M1 1l10 10M11 1L1 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => {
            const isActive = filter === c;
            const count = categoryCounts[c === "ALL" ? "ALL" : c];
            return (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors",
                  isActive
                    ? " bg-litmus-dark-green-800 text-white"
                    : "border border-rule bg-surface text-ash hover:border-datahub-gray-1700/30 hover:text-ink",
                )}
              >
                {c === "ALL" ? "All" : c}
                {count !== undefined && (
                  <span
                    className={cn(
                      "rounded px-1 py-0 text-[10px] font-semibold leading-4",
                      isActive
                        ? "bg-litmus-light-green-700 text-white"
                        : "bg-datahub-gray-1500 text-ash",
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* GRID */}
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {connectors.isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card h-48 animate-pulseSoft" />
          ))}
        {!connectors.isLoading && filtered.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
            <svg className="mb-3 h-8 w-8 text-ash/40" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M16.5 16.5L20 20"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <p className="text-[13.5px] text-ash">
              No connectors match{search ? ` "${search}"` : ""}.
            </p>
          </div>
        )}
        {filtered.map((c) => {
          const info = CONNECTOR_INFO[c.id] ?? {
            category: "Other",
            tagline: "Custom connector.",
          };
          const tone = CATEGORY_TONE[info.category] ?? CATEGORY_TONE.Generic;
          const icon = CATEGORY_ICONS[info.category] ?? CATEGORY_ICONS.Generic;
          const inUse = sourceCountByConnector.get(c.id) ?? 0;

          return (
            <article
              key={c.id}
              className="card card-hover group relative flex flex-col overflow-hidden"
            >
              {/* Card header */}
              <div className="flex items-start gap-3 p-5 pb-4">
                {/* Logo tile: white bg for real logos (mix-blend-mode removes
                    any baked-in PNG background); category color for icon fallback */}
                <div
                  className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg overflow-hidden",
                    getPlatformLogo(c.id) ? "border border-rule bg-white" : tone.icon,
                  )}
                >
                  {getPlatformLogo(c.id) ? (
                    <PlatformLogo platformId={c.id} alt={c.displayName} className="h-7 w-7" />
                  ) : (
                    icon
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[15px] font-semibold leading-snug tracking-tight text-ink">
                      {c.displayName}
                    </h3>
                    <span
                      className={cn(
                        "shrink-0 rounded-full border px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider",
                        tone.chip,
                      )}
                    >
                      {info.category}
                    </span>
                  </div>
                  <div className="mt-0.5 font-mono text-[10.5px] text-ash/70">{c.id}</div>
                </div>
              </div>

              {/* Tagline */}
              <p className="px-5 text-[13px] leading-relaxed text-ash">{info.tagline}</p>

              {/* Schema fields */}
              {c.schema.fields.length > 0 && c.id !== "generic_ot_metadata" ? (
                <div className="mx-5 mt-4 rounded-md bg-datahub-gray-1500 px-3 py-2.5">
                  <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.14em] text-ash">
                    Connection fields
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {c.schema.fields.slice(0, 5).map((f) => (
                      <span
                        key={f.key}
                        className={cn(
                          "rounded border px-1.5 py-0.5 font-mono text-[10px]",
                          f.type === "secret"
                            ? "border-rose-200 bg-rose-50 text-rose-600"
                            : "border-rule bg-surface text-ash",
                        )}
                        title={f.help ?? ""}
                      >
                        {f.key}
                        {f.required ? "*" : ""}
                      </span>
                    ))}
                    {c.schema.fields.length > 5 && (
                      <span className="font-mono text-[10px] text-ash/60">
                        +{c.schema.fields.length - 5}
                      </span>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Footer */}
              <div className="mt-auto flex items-center justify-between border-t border-rule px-5 py-3">
                <div className="flex items-baseline gap-1">
                  <span
                    className="font-display text-[20px] font-semibold leading-none text-ink"
                    data-num
                  >
                    {inUse}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ash">
                    {inUse === 1 ? "source" : "sources"}
                  </span>
                </div>
                <div className="flex gap-2">
                  {c.id === "generic_ot_metadata" && (
                    <button
                      onClick={downloadGenericTemplate}
                      className="btn-secondary text-[12px] py-1.5 px-3"
                    >
                      Template
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (c.id === "litmus") {
                        setAddingFor(c);
                      } else {
                        setConfiguringConnector(c);
                      }
                    }}
                    className="btn-primary-outline text-[12px] py-1.5 px-3"
                  >
                    Add Source →
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {/* Litmus Edge: schema-driven dialog with its own scheduled flow */}
      {addingFor && (
        <AddSourceDialog
          open={!!addingFor}
          onOpenChange={(o) => {
            if (!o) setAddingFor(null);
          }}
          presetConnectorId={addingFor.id}
        />
      )}

      {/* All other connectors: universal modal with scheduled sync + SHA-256 detection */}
      {configuringConnector && (
        <ConfigureConnectorModal
          open
          onOpenChange={(o) => {
            if (!o) setConfiguringConnector(null);
          }}
          connectorSummary={configuringConnector}
          onPickDifferentConnector={() => setConfiguringConnector(null)}
        />
      )}
    </div>
  );
}
